import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Steps, message, Spin } from "antd";
import axios from "axios";
import { API_CONST } from "../../../../const";
import { AddHiringRecordWrapper } from "./AddHiringRecord.style";
import TalentSelection from "./TalentSelection";
import ContractDetails from "./ContractDetails";

const AddHiringRecord = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    partnerOrganization: "",
    talentId: "",
    talentProfile: "",
    talentEmail: "",
    talentBudget: "",
    talentLocation: "",
    customerOrganization: "",
    jobId: "",
    jobTitle: "",
    onboardingDate: null,
    contractDurationMonths: "",
    lastWorkingDay: null,
    monthlyRate: "",
    clientRate: "",
    settledCost: "",
    notes: "",
  });

  useEffect(() => {
    if (isEditMode) {
      fetchHiringRecord();
    }
  }, [id]);

  const fetchHiringRecord = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${API_CONST.GET_HIRING_RECORD}/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const record = response.data.hiringRecord;
      setFormData({
        partnerOrganization: record.partnerOrganization,
        talentId: record.talentId,
        talentProfile: record.talentId,
        talentEmail: record.talent?.email || "",
        talentBudget: record.monthlyRate,
        talentLocation: record.talent?.location || "",
        customerOrganization: record.customerOrganization,
        jobId: record.jobId,
        jobTitle: record.job?.role || "",
        onboardingDate: record.onboardingDate,
        contractDurationMonths: record.contractDurationMonths,
        lastWorkingDay: record.lastWorkingDay,
        monthlyRate: record.monthlyRate,
        clientRate: record.clientRate,
        settledCost: record.settledCost,
        notes: record.notes || "",
      });
    } catch (error) {
      console.error("Error fetching hiring record:", error);
      message.error("Failed to load hiring record");
      navigate("/admin/talents-hired");
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    {
      title: "Talent Selection",
      component: TalentSelection,
    },
    {
      title: "Contract Details",
      component: ContractDetails,
    },
  ];

  const handleNext = (data) => {
    setFormData({ ...formData, ...data });
    setCurrentStep(currentStep + 1);
  };

  const handleBack = (data = {}) => {
    setFormData({ ...formData, ...data });
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async (data) => {
    const finalData = { ...formData, ...data };
    setSubmitting(true);
    
    try {
      const token = localStorage.getItem("authToken");
      const payload = {
        jobId: finalData.jobId,
        talentId: finalData.talentId,
        partnerOrganization: finalData.partnerOrganization,
        customerOrganization: finalData.customerOrganization,
        monthlyRate: parseFloat(finalData.monthlyRate),
        clientRate: finalData.clientRate ? parseFloat(finalData.clientRate) : undefined,
        settledCost: finalData.settledCost ? parseFloat(finalData.settledCost) : undefined,
        onboardingDate: finalData.onboardingDate,
        contractDurationMonths: parseInt(finalData.contractDurationMonths),
        lastWorkingDay: finalData.lastWorkingDay || undefined,
        notes: finalData.notes || undefined,
      };

      if (isEditMode) {
        await axios.put(
          `${API_CONST.UPDATE_HIRING_RECORD}/${id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        message.success("Hiring record updated successfully");
      } else {
        await axios.post(
          API_CONST.ADD_HIRING_RECORD,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        message.success("Hiring record created successfully");
      }
      navigate("/admin/talents-hired");
    } catch (error) {
      console.error("Error saving hiring record:", error);
      message.error(error.response?.data?.message || "Failed to save hiring record");
    } finally {
      setSubmitting(false);
    }
  };

  const CurrentStepComponent = steps[currentStep].component;

  if (loading) {
    return (
      <AddHiringRecordWrapper>
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: 400 }}>
          <Spin size="large" />
        </div>
      </AddHiringRecordWrapper>
    );
  }

  return (
    <AddHiringRecordWrapper>
      <div className="page-header">
        <h1>{isEditMode ? "Edit Hiring Record" : "Add Hiring Record"}</h1>
      </div>

      <div className="form-container">
        <div className="steps-container">
          <Steps current={currentStep} items={steps.map((step) => ({ title: step.title }))} />
        </div>

        <div className="form-card">
          <Spin spinning={submitting}>
            <CurrentStepComponent
              initialData={formData}
              onNext={handleNext}
              onBack={handleBack}
              onSubmit={handleSubmit}
              isFirstStep={currentStep === 0}
              isLastStep={currentStep === steps.length - 1}
              isEditMode={isEditMode}
            />
          </Spin>
        </div>
      </div>
    </AddHiringRecordWrapper>
  );
};

export default AddHiringRecord;
